// SGNL-208 — Auth guards for the SPA's affordances.
//
// Per the WhoAmI.is_admin comment in types.ts, the correct read
// pattern is `user?.is_admin ?? false` — never `if (user.is_admin)`,
// since `undefined` and `false` would diverge in any falsy-OR chain.
// Centralising the check here also gives Arch F-05 a single source
// of truth: if the admin policy evolves (e.g. checking a role array
// instead of a boolean flag), only this module changes.

import { getState } from "./state";

/** True iff the current user is an admin per the /api/whoami payload.
 * Safe against pre-SGNL-147 cached SPA bundles that omit is_admin
 * (treats missing as non-admin — the safer default). */
export function isCurrentUserAdmin(): boolean {
  return getState().user?.is_admin ?? false;
}

/** SGNL-208 — hide the Admin tab from non-admin users.
 *
 * The tab buttons are rendered as static HTML in index.html (no JS
 * conditional on the server side). Without this gate, non-admin users
 * see a clickable Admin tab that 403s on every sub-route (audit /
 * users / apps). Removal also takes the button out of keyboard tab
 * order without needing aria-hidden / tabindex wiring.
 *
 * Idempotent: re-running is a no-op (the querySelector returns null
 * if the button is already removed). Used in init() AND can be safely
 * called from any future session-refresh handler.
 *
 * Defence-in-depth, not the primary security boundary. The BE remains
 * the source of truth — every admin endpoint enforces is_admin and
 * returns 403 regardless of whether the FE rendered the tab.
 *
 * @param tabsNav The .tabs container element. Passed in for testability
 *   (the production caller passes `document.querySelector('.tabs')`).
 */
export function gateAdminTab(tabsNav: HTMLElement): void {
  if (isCurrentUserAdmin()) return;
  const adminBtn = tabsNav.querySelector<HTMLButtonElement>(
    '.tab[data-tab="admin"]',
  );
  if (adminBtn) adminBtn.remove();
}
